import baseApi from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import {
  GetChartDataParams,
  GetChartDataResponse,
} from 'types/Meteorology/Agro/TrackingForecast/DetailedView/getChartData';
import {
  transformGetTimeFrequencies,
  transformGetVariables,
  transformGetDates,
  transformListPreferences,
} from 'helper/Meteorology/Agro/TrackingForecast/DetailedView';
import {
  SavePreferenceParams,
  SavePreferenceResponse,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/savePreference';
import {
  DeletePreferenceParams,
  DeletePreferenceResponse,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/deletePreference';
import {
  GetTimeFrequenciesParams,
  TimeFrequency,
} from 'types/Meteorology/Agro/TrackingForecast/DetailedView/getTimeFrequencies';
import { GetVariablesParams, Variable } from 'types/Meteorology/Agro/TrackingForecast/DetailedView/variable';
import { GetDatesParams } from 'types/Meteorology/Agro/TrackingForecast/DetailedView/getDates';
import { AgroTrackingForecastPreference } from 'types/Meteorology/Agro/TrackingForecast/DetailedView/preference';
import { SUBPAGE_SUBMENU_OPTIONS } from 'types/Meteorology/Agro/TrackingForecast/DetailedView';

/**
 * Retenção de cache das listas ESTRUTURAIS do painel (5 min, contra o default de
 * 60s do RTK Query) — mesma decisão do equivalente em Fields.
 *
 * Só entram as listas de CONFIGURAÇÃO (quais frequências e variáveis existem), que
 * não mudam quando entra rodada nova. `data-previsao` e `chart/data` ficam FORA de
 * propósito: a lista de datas alimenta o default "data mais recente" e segurá-la em
 * cache faria a tela abrir numa rodada velha após o backend publicar uma nova.
 */
const STRUCTURAL_OPTIONS_CACHE_SECONDS = 300;

export const agroTrackingForecastDetailedViewApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAgroTrackingForecastTimeFrequencies: build.query<TimeFrequency[], GetTimeFrequenciesParams>({
      query: ({ submenu }) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/frequencias-temporais/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
          },
        };
      },
      transformResponse: transformGetTimeFrequencies,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getAgroTrackingForecastDetailedViewVariables: build.query<Variable[], GetVariablesParams>({
      query: ({
        submenu,
        timeFrequency,
        modelIds,
        forecastDate,
        locationId,
      }) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/variaveis/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
            frequencia_temporal: timeFrequency,
            id_modelos: modelIds,
            data_previsao: forecastDate,
            id_local: locationId,
          },
        };
      },
      transformResponse: transformGetVariables,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getAgroTrackingForecastDetailedViewDates: build.query<string[], GetDatesParams>({
      query: ({
        locationId,
        locationIds,
        submenu,
        timeFrequency,
        variable,
      }) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/data-previsao/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
            tipo_produto: 'agro',
            id_local: locationId,
            ids_locais: locationIds,
            frequencia_temporal: timeFrequency,
            id_modelo: 1,
            variavel: variable,
          },
        };
      },
      transformResponse: transformGetDates,
    }),
    getAgroTrackingForecastChartData: build.query<GetChartDataResponse, GetChartDataParams>({
      query: (params) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/local-previsao/chart/data?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu: SUBPAGE_SUBMENU_OPTIONS[params.subpage],
            tipo_produto: 'agro',
            chart_type: params.chartType,
            frequencia_temporal: params.timeFrequency,
            variavel: params.variable,
            id_local: params.locationId,
            lst_locais: params.locationIds,
            lst_modelos: [1],
            lst_datas_rodada: params.forecastDates,
          },
        };
      },
    }),
    saveAgroTrackingForecastPreference: build.mutation<
    SavePreferenceResponse,
    SavePreferenceParams
    >({
      query: (params) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/visao-detalhada/preferencias/save/?product_key=${productKey}`,
          method: 'POST',
          body: {
            name: params.name,
            json_p: {
              submenu: SUBPAGE_SUBMENU_OPTIONS[params.subpage],
              ...params.preferenceParams,
            },
            popular: false,
          },
        };
      },
      invalidatesTags: ['AgroTrackingForecastDetailedViewPreferences'],
    }),
    deleteAgroTrackingForecastPreference: build.mutation<
    DeletePreferenceResponse,
    DeletePreferenceParams
    >({
      query: (params) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/visao-detalhada/preferencias/delete/?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            name: params.name,
          },
        };
      },
      invalidatesTags: ['AgroTrackingForecastDetailedViewPreferences'],
    }),
    listAgroTrackingForecastPreferences: build.query<
    AgroTrackingForecastPreference[],
    void
    >({
      query: () => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/visao-detalhada/preferencias/get/?product_key=${productKey}`,
          method: 'POST',
        };
      },
      transformResponse: transformListPreferences,
      providesTags: ['AgroTrackingForecastDetailedViewPreferences'],
    }),
  }),
});

export const {
  useGetAgroTrackingForecastTimeFrequenciesQuery,
  useGetAgroTrackingForecastDetailedViewDatesQuery,
  useLazyGetAgroTrackingForecastDetailedViewDatesQuery,
  useGetAgroTrackingForecastDetailedViewVariablesQuery,
  useLazyGetAgroTrackingForecastDetailedViewVariablesQuery,
  useGetAgroTrackingForecastChartDataQuery,
  useLazyGetAgroTrackingForecastChartDataQuery,
  useSaveAgroTrackingForecastPreferenceMutation,
  useDeleteAgroTrackingForecastPreferenceMutation,
  useListAgroTrackingForecastPreferencesQuery,
} = agroTrackingForecastDetailedViewApi;
